/**
 * Persistence for the Credit Tele Verification Report.
 *
 * Values live in the real TVR API (`/case/:id/tvr-details/`): GET on load (the
 * backend builds the record lazily when absent), then ONE PATCH per subsection
 * when the analyst submits it. PATCH deep-merges, so each submit sends only the
 * branches it actually changed.
 *
 * NOTE: there is no per-field audit trail. The API has no audit endpoint, and the
 * previous browser-local (localStorage) stand-in was removed — it was invisible to
 * other users and did not survive a cache clear, so it was not a real audit. When
 * a backend audit table exists, `saveTvrFields` is the single place to record to.
 *
 * Shapes:
 *   loadTvr(caseId)    -> { tvr }
 *   saveTvrFields(...) -> { tvr }
 */

import {
    fetchCaseTvrDetails,
    patchCaseTvrDetails,
    acceptTvrSuggestions as acceptTvrSuggestionsApi,
    rejectTvrSuggestions as rejectTvrSuggestionsApi
} from "services/case-service";
import { TVR_SUBSECTIONS, TVR_SUGGESTION_ENUM_OPTIONS } from "./tvr-config";

// Reads a dotted path out of the TVR object. Returns null for any missing link,
// so a half-built record (the API omits untouched branches) reads as empty.
export function readPath(obj, path) {
    if (!obj || !path) {
        return null;
    }
    const value = path
        .split(".")
        .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
    return value === undefined ? null : value;
}

// A single mandatory TVR field is "empty" when the stored record has no value
// for it. Mirrors isEmptyValue in updated-tvr-section, but reads the stored
// record only (no draft state) — used by the proceed checklist.
function isTvrFieldEmpty(tvr, field) {
    const v = readPath(tvr, field.path);
    if (field.type === "list") {
        return !Array.isArray(v) || v.length === 0;
    }
    return v === null || v === undefined || v === "";
}

// Every mandatory TVR field still unfilled in the stored record, in the same
// declaration order the submit-bar warning uses.
export function getEmptyTvrFields(tvr) {
    return TVR_SUBSECTIONS.reduce(
        (acc, section) =>
            acc.concat(section.fields.filter(f => isTvrFieldEmpty(tvr, f))),
        []
    );
}

// TVR is complete when a record exists and no mandatory field is empty. A null
// record (never loaded / GET failed) reads as incomplete.
export function isTvrComplete(tvr) {
    return !!tvr && getEmptyTvrFields(tvr).length === 0;
}

// Builds the minimal nested object for a single dotted path — the PATCH body.
// "a.b.c" + 1 -> { a: { b: { c: 1 } } }. Safe because PATCH deep-merges.
export function buildPatch(path, value) {
    return path.split(".").reduceRight((acc, key) => ({ [key]: acc }), value);
}

/**
 * Coerces a form control's output into the type the API expects. The form talks
 * in strings; the API stores typed scalars, so an empty control must become null
 * (not "") and a numeric control must become a number (not "12,50,000").
 */
export function coerceValue(field, value) {
    // Repeating groups: lists are REPLACED wholesale by PATCH, so the whole array
    // goes over. Blank rows are dropped rather than stored as rows of nulls, and
    // an emptied list sends [] (which is how the API clears a group).
    if (field.type === "list") {
        const columns = field.columns || [];
        const rows = Array.isArray(value) ? value : [];
        return rows
            .filter(
                row =>
                    !columns.every(col => {
                        const v = row ? row[col.key] : null;
                        return v === null || v === undefined || v === "";
                    })
            )
            .map(row => {
                const out = {};
                columns.forEach(col => {
                    const v = row[col.key];
                    if (v === null || v === undefined || v === "") {
                        out[col.key] = null;
                    } else if (col.type === "number" || col.type === "currency") {
                        const n = Number(String(v).replace(/[^0-9.-]/g, ""));
                        out[col.key] =
                            isFinite(n) && !Number.isNaN(n) ? n : null;
                    } else {
                        out[col.key] = String(v);
                    }
                });
                return out;
            });
    }
    if (value === null || value === undefined || value === "") {
        return null;
    }
    if (field.type === "number" || field.type === "currency") {
        const n = Number(String(value).replace(/[^0-9.-]/g, ""));
        return isFinite(n) && !Number.isNaN(n) ? n : null;
    }
    if (field.type === "boolean") {
        return typeof value === "boolean" ? value : String(value) === "true";
    }
    return String(value);
}

// Recursively merges `source` into `target` without mutating either — used to
// fold many single-path patches into one section-wide PATCH body.
function deepMerge(target, source) {
    const out = { ...target };
    Object.keys(source).forEach(key => {
        const next = source[key];
        const prev = out[key];
        const bothPlainObjects =
            next && typeof next === "object" && !Array.isArray(next) &&
            prev && typeof prev === "object" && !Array.isArray(prev);
        out[key] = bothPlainObjects ? deepMerge(prev, next) : next;
    });
    return out;
}

/**
 * Loads the stored TVR record. A failed GET resolves with a null `tvr` rather
 * than rejecting, so the section still renders (empty, editable) instead of
 * blanking out.
 */
export function loadTvr(caseId) {
    return fetchCaseTvrDetails(caseId)
        .then(res => ({ tvr: (res && res.data) || null }))
        .catch(() => ({ tvr: null }));
}

/**
 * Commits a whole subsection's edits: ONE PATCH carrying every changed field.
 * Committing per section (rather than per field) means a half-finished section
 * never reaches the backend.
 *
 * @param {number|string} caseId
 * @param {Array}  changes  [{ field, value }] — changed fields only
 * @returns {Promise<{tvr: Object}>}
 */
export function saveTvrFields(caseId, changes) {
    const payload = changes.reduce(
        (acc, { field, value }) =>
            deepMerge(acc, buildPatch(field.path, coerceValue(field, value))),
        {}
    );
    return patchCaseTvrDetails(caseId, payload).then(res => ({
        tvr: (res && res.data) || null
    }));
}

/**
 * Reset — rebuilds the TVR from source and OVERWRITES the stored record,
 * discarding every manually entered value (GET `?force=true`). Destructive, so the
 * caller confirms first. The backend gates this to the credit stages, returning
 * the freshly rebuilt TVR.
 *
 * @param {number|string} caseId
 * @returns {Promise<{tvr: Object}>}
 */
export function resetTvr(caseId) {
    return fetchCaseTvrDetails(caseId, true).then(res => ({
        tvr: (res && res.data) || null
    }));
}

// Dotted path to the ``suggestion_<leaf>`` sibling Chitthi writes.
export function suggestionPath(answerPath) {
    const parts = answerPath.split(".");
    const leaf = parts.pop();
    return parts.length
        ? `${parts.join(".")}.suggestion_${leaf}`
        : `suggestion_${leaf}`;
}

export function parseSuggestion(raw) {
    if (raw == null || raw === "") {
        return null;
    }
    if (typeof raw === "string") {
        const value = raw.trim();
        return value ? { value, confidence: 0.7, evidence: "" } : null;
    }
    if (typeof raw === "object") {
        const value = (raw.value != null ? String(raw.value) : "").trim();
        if (!value) {
            return null;
        }
        const confidence =
            typeof raw.confidence === "number" && !Number.isNaN(raw.confidence)
                ? raw.confidence
                : 0.7;
        return {
            value,
            confidence: Math.max(0, Math.min(1, confidence)),
            evidence: (raw.evidence || "").trim()
        };
    }
    return null;
}

export function suggestionFor(tvr, field) {
    if (!field.suggestable) {
        return null;
    }
    return parseSuggestion(readPath(tvr, suggestionPath(field.path)));
}

function storedMatchesSuggestion(tvr, field, suggestion) {
    const stored = readPath(tvr, field.path);
    if (stored == null || stored === "") {
        return false;
    }
    return String(stored).trim() === suggestion.value.trim();
}

// Closed-list fields (select / combo) — suggestion must match a dropdown option.
export function allowedSuggestionOptions(field, optionsFor) {
    if (!field || !field.suggestable) {
        return null;
    }
    if (field.type === "select") {
        return (
            TVR_SUGGESTION_ENUM_OPTIONS[field.path] ||
            field.options ||
            null
        );
    }
    if (field.type === "combo" && field.optionsFrom === "applicants") {
        return optionsFor ? optionsFor(field) : null;
    }
    return null;
}

export function suggestionMatchesOptions(field, suggestion, optionsFor) {
    const allowed = allowedSuggestionOptions(field, optionsFor);
    if (!allowed || !Array.isArray(allowed) || !allowed.length) {
        return true;
    }
    const value = (suggestion && suggestion.value ? String(suggestion.value) : "").trim();
    if (!value) {
        return false;
    }
    const norm = v => String(v).trim().toLowerCase();
    const target = norm(value);
    return allowed.some(opt => norm(opt) === target);
}

export function shouldShowSuggestion(tvr, field, dismissedPaths, optionsFor) {
    const suggestion = suggestionFor(tvr, field);
    if (!suggestion) {
        return false;
    }
    if (dismissedPaths && dismissedPaths.has(field.path)) {
        return false;
    }
    if (storedMatchesSuggestion(tvr, field, suggestion)) {
        return false;
    }
    if (!suggestionMatchesOptions(field, suggestion, optionsFor)) {
        return false;
    }
    return true;
}

export function listPendingSuggestionFields(tvr, dismissedPaths, optionsFor) {
    return TVR_SUBSECTIONS.reduce((acc, section) => {
        section.fields.forEach(field => {
            if (shouldShowSuggestion(tvr, field, dismissedPaths, optionsFor)) {
                acc.push(field);
            }
        });
        return acc;
    }, []);
}

export function countPendingSuggestions(tvr, dismissedPaths, optionsFor) {
    return listPendingSuggestionFields(tvr, dismissedPaths, optionsFor).length;
}

export function acceptTvrSuggestions(caseId, paths) {
    return acceptTvrSuggestionsApi(caseId, paths).then(res => ({
        tvr: (res && res.data && res.data.tvr_details) || null,
        accepted: (res && res.data && res.data.accepted_fields) || [],
        missing: (res && res.data && res.data.missing_fields) || []
    }));
}

export function rejectTvrSuggestions(caseId, paths) {
    return rejectTvrSuggestionsApi(caseId, paths).then(res => ({
        tvr: (res && res.data && res.data.tvr_details) || null,
        rejected: (res && res.data && res.data.rejected_fields) || []
    }));
}

export default {
    loadTvr,
    saveTvrFields,
    resetTvr,
    readPath,
    buildPatch,
    coerceValue,
    suggestionPath,
    parseSuggestion,
    suggestionFor,
    allowedSuggestionOptions,
    suggestionMatchesOptions,
    shouldShowSuggestion,
    listPendingSuggestionFields,
    countPendingSuggestions,
    acceptTvrSuggestions,
    rejectTvrSuggestions
};
